<template>
  <div class="filter-set">
    <button
      class="filter-name"
      @click.stop="$emit('toggle')"
      :class="{ 'in-use': currentMin || currentMax }"
      :aria-expanded="expanderVisible ? 'true' : 'false'"
      :aria-controls="filterName + '-slider-panel'"
      :id="filterName + '-toggle'"
    >
      <span class="filter-name__label">{{ label }}</span>
      <i class="toggle-expander icon-chevron-down"></i>
    </button>
    <expander :expanded="expanderVisible">
      <div
        class="slider-input"
        v-if="sliderConf"
        :id="filterName + '-slider-panel'"
        role="region"
        :aria-labelledby="filterName + '-area-toggle'"
      >
        <form-slider
          :id="filterName"
          :conf="sliderConf"
          @changed="rangeChanged($event)"
          :aria-label="ariaLabel"
        ></form-slider>
      </div>
    </expander>
  </div>
</template>

<script setup lang="ts">
import Expander from '../expander/expander.vue';
import FormSlider from '../form-slider/form-slider.vue';
import searchUtils from '@/app/utils/search-utils';
import logger from '@/app/logger';
import { computed, toRefs } from 'vue';
import useSafeRoute from '@/app/composables/useSafeRoute';
import { useRouter } from 'vue-router';
import type { SliderConf } from '@/app/components/form-slider/form-slider.vue';

const props = withDefaults(
  defineProps<{
    expanderVisible?: boolean;
    filterName: string;
    label: string;
    limitMin?: number;
    limitMax: number;
    currentMin?: number;
    currentMax?: number;
    unit?: string;
    step?: number;
    ariaLabel?: string;
  }>(),
  {
    expanderVisible: undefined, // By default, the expander is visible on mobile, but without any complex close/open css logic
    limitMin: 0,
    step: 1,
  }
);

const route = useSafeRoute();
const router = useRouter();

const { expanderVisible, filterName, label, limitMin, limitMax, currentMin, currentMax, unit, step, ariaLabel } =
  toRefs(props);

const sliderConf = computed<SliderConf>(() => {
  return {
    unit: unit.value,
    start: [
      typeof currentMin.value === 'number' ? currentMin.value : limitMin.value,
      typeof currentMax.value === 'number' ? currentMax.value : limitMax.value,
    ],
    step: step.value,
    range: {
      min: limitMin.value,
      max: limitMax.value,
    },
  };
});

const rangeChanged = (values: number[]) => {
  if (values && Array.isArray(values) && values.length === 2) {
    const updates: Record<string, number> = {};
    updates[`${filterName.value}Min`] = values[0];
    updates[`${filterName.value}Max`] = values[1];
    searchUtils.addFilterToUrl(router, route.value, updates);
  } else {
    logger.info('Invalid values for area range slider: ', { values });
  }
};
</script>
